import { useTranslation } from 'react-i18next';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { styled } from '@mui/material/styles';
import { CardItem, ObjectiveKpiCard, type ObjectiveKpiAccent } from '@bloomays-lib/ui.shared';
import type { ContractSegment } from '@bloomays-lib/utils.shared';

export type ContractsKpiCardSegmentValue = {
  toSign: number;
  late: number;
  missionsConcerned?: number;
};

export type ContractsKpiCardsSegments = Record<ContractSegment, ContractsKpiCardSegmentValue>;

export type ContractsKpiCardsProps = {
  segments: ContractsKpiCardsSegments;
  onSegmentChange: (segment: ContractSegment) => void;
};

type SegmentDefinition = {
  segment: ContractSegment;
  titleKey: string;
  accent: ObjectiveKpiAccent;
  icon: 'edit' | 'bolt';
  showLateTag: boolean;
};

const SEGMENT_DEFINITIONS: SegmentDefinition[] = [
  { segment: 'contractSst', titleKey: 'kpiCards.contractSstTitle', accent: 'blue', icon: 'edit', showLateTag: true },
  { segment: 'contractPds', titleKey: 'kpiCards.contractPdsTitle', accent: 'pink', icon: 'edit', showLateTag: true },
  {
    segment: 'amendmentSst',
    titleKey: 'kpiCards.amendmentSstTitle',
    accent: 'blue',
    icon: 'bolt',
    showLateTag: false,
  },
  {
    segment: 'amendmentPds',
    titleKey: 'kpiCards.amendmentPdsTitle',
    accent: 'pink',
    icon: 'bolt',
    showLateTag: false,
  },
];

const ContractsKpiCards = ({ segments, onSegmentChange }: ContractsKpiCardsProps) => {
  const { t } = useTranslation(['legalContracts']);

  return (
    <CardsRow>
      {SEGMENT_DEFINITIONS.map(({ segment, titleKey, accent, icon, showLateTag }) => {
        const { toSign, late, missionsConcerned } = segments[segment];
        const footer =
          toSign === 0
            ? t('kpiCards.nothingToSign')
            : missionsConcerned !== undefined
            ? t('kpiCards.missionsCount', { count: missionsConcerned })
            : t('kpiCards.documentsCount', { count: toSign });

        return (
          <CardSlot key={segment}>
            <ObjectiveKpiCard
              data-testid={`kpi-${segment}`}
              accent={accent}
              icon={
                <CardItem
                  variant={accent === 'blue' ? 'blue' : 'pink'}
                  size="small"
                  emoji={icon === 'edit' ? <EditRoundedIcon fontSize="small" /> : <BoltRoundedIcon fontSize="small" />}
                />
              }
              title={t(titleKey)}
              valuePrimary={String(toSign)}
              progressPercent={toSign > 0 ? 100 : 0}
              footer={footer}
              footerAccent={toSign > 0}
              footerTag={
                showLateTag && late > 0 ? { label: t('kpiCards.lateTag', { count: late }), tone: 'error' } : undefined
              }
              onClick={() => onSegmentChange(segment)}
            />
          </CardSlot>
        );
      })}
    </CardsRow>
  );
};

export default ContractsKpiCards;

const CardsRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 16,
  marginBottom: 16,
}));

const CardSlot = styled('div')(() => ({
  flex: '1 1 220px',
  minWidth: 220,
}));
